/*
  bf_turncombat.js
  Copyright (c) 2013-present Human Cube Inc.

  Does all zone combat.  Must all be synchronous and not involve any external DB or file access.
  Also does not access any external user data.
  These restrictions are to allow it to be re-used all environments such as the client.
*/

import BF from '../bfconst1';
import bfH from '../bf_helper';
import { Game } from '../gameTypes';
import bfTurnReplay from './bf_turnreplay';


export const doCombat = (game: Game) => {
  for(let zoneID = 1; zoneID <= game.zones; zoneID++) {
    doZoneCombat(game, zoneID);
  }
};


function doZoneCombat (game: Game, zoneID: number) {
  // Do combat for a single zone.

  const players = game.players;

  // Air target, Surface targets.  Array of forces for each player.
  const targetAir: number[][][] = [];
  const targetSurface: number[][][] = [];

  // Air only shooter, surface only shooter, and shoot any shooter.
  // Array of forces for each player.
  const shootAir: number[][][] = [];
  const shootSurface: number[][][] = [];
  const shootAny: number[][][] = [];

  let zoneOwner = -1;
  if( bfH.isLand(game, zoneID) ) {
    zoneOwner = game.zoneOwner[zoneID];
  }

  let thereIsCombat = false;  // Set to true if there is actual confrontation.
  let forceIDOfDestroyAll = 0;  // ForceID of a nuke like weapon involved, that destroys all.
  for(let playerIndex = 0; playerIndex <= players; playerIndex++) {  // All players and 0.
    let len = game.force[playerIndex].length;
    for(let j = 0; j < len; j++) {
      let force = game.force[playerIndex][j];
      if( force[BF.FORCE_INDEX_ZONE] === zoneID ) {
        const forceType = force[BF.FORCE_INDEX_TYPE];
        if( game.forceFlags[forceType] & BF.FORCE_FLAG_DESTROYS_ALL ) {
          forceIDOfDestroyAll = force[BF.FORCE_INDEX_ID];
        }
      }
    }
  }

  for(let playerIndex = 0; playerIndex <= players; playerIndex++) {  // All players and 0.
    targetAir[playerIndex] = [];
    targetSurface[playerIndex] = [];

    shootAir[playerIndex] = [];
    shootSurface[playerIndex] = [];
    shootAny[playerIndex] = [];

    let len = game.force[playerIndex].length;
    for(let j = 0; j < len; j++) {
      let force = game.force[playerIndex][j];
      if( force[BF.FORCE_INDEX_ZONE] === zoneID ) {
        let forceType = force[BF.FORCE_INDEX_TYPE];
        let forceFlags = game.forceFlags[forceType];

        if( forceFlags & BF.FORCE_FLAG_IS_AIR_ONLY_TARGET )
          targetAir[playerIndex].push(force);
        else if( forceFlags & BF.FORCE_FLAG_IS_SURFACE_ONLY_TARGET )
          targetSurface[playerIndex].push(force);

        if( forceFlags & BF.FORCE_FLAG_HITS_ONLY_AIR_TARGETS )
          shootAir[playerIndex].push(force);
        else if( forceFlags & BF.FORCE_FLAG_HITS_ONLY_SURFACE_TARGETS )
          shootSurface[playerIndex].push(force);
        else shootAny[playerIndex].push(force);
      }
    }
  }

  // Do all air only shooters:
  for(let playerIndex = 0; playerIndex <= players; playerIndex++) {  // All players and 0.
    if( shootAir[playerIndex].length === 0 ) {
      continue;
    }

    let opponents = 0;
    const targets: number[][][] = [];
    for(let opponentIndex = 0; opponentIndex <= players; opponentIndex++) {  // All and 0.
      if( !(game.fixedAlly[playerIndex] & (1 << opponentIndex)) ) {
        opponents += targetAir[opponentIndex].length;
        targets[opponentIndex] = targetAir[opponentIndex];
      }
      else {
        targets[opponentIndex] = [];
      }
    }
    if( opponents === 0 ) {
      continue;  // Nothing to shoot at.
    }
    thereIsCombat = true;

    let isDefender = false;
    if( zoneOwner >= 0 && (game.fixedAlly[zoneOwner] & (1 << playerIndex)) ) {
      isDefender = true;
    }

    takeShots(game, isDefender, shootAir[playerIndex], opponents, targets);
  }

  // Do all surface only shooters:
  for(let playerIndex = 0; playerIndex <= players; playerIndex++) {  // All players and 0.
    if( shootSurface[playerIndex].length === 0 ) {
      continue;
    }

    let opponents = 0;
    const targets: number[][][] = [];
    for(let opponentIndex = 0; opponentIndex <= players; opponentIndex++) {  // All and 0.
      if( !(game.fixedAlly[playerIndex] & (1 << opponentIndex)) ) {
        opponents += targetSurface[opponentIndex].length;
        targets[opponentIndex] = targetSurface[opponentIndex];
      }
      else {
        targets[opponentIndex] = [];
      }
    }
    if( opponents === 0 ) {
      continue;  // Nothing to shoot at.
    }
    thereIsCombat = true;

    let isDefender = false;
    if( zoneOwner >= 0 && (game.fixedAlly[zoneOwner] & (1 << playerIndex)) ) {
      isDefender = true;
    }

    takeShots(game, isDefender, shootSurface[playerIndex], opponents, targets);
  }

  // Do all Any shooters:
  for(let playerIndex = 0; playerIndex <= players; playerIndex++) {  // All players and 0.
    if( shootAny[playerIndex].length === 0 ) {
      continue;  // Nothing that can shoot.
    }

    let opponents = 0;
    const targets: number[][][] = [];
    for(let opponentIndex = 0; opponentIndex <= players; opponentIndex++) {  // All and 0.
      if( !(game.fixedAlly[playerIndex] & (1 << opponentIndex)) ) {
        opponents += targetAir[opponentIndex].length + targetSurface[opponentIndex].length;
        targets[opponentIndex] = targetAir[opponentIndex].concat(targetSurface[opponentIndex]);
      }
      else {
        targets[opponentIndex] = [];
      }
    }
    if( opponents === 0 ) {
      continue;  // Nothing to shoot at.
    }
    thereIsCombat = true;

    let isDefender = false;
    if( zoneOwner >= 0 && (game.fixedAlly[zoneOwner] & (1 << playerIndex)) ) {
      isDefender = true;
    }

    takeShots(game, isDefender, shootAny[playerIndex], opponents, targets);
  }

  if(thereIsCombat) {
    if(forceIDOfDestroyAll) {
      // Destroy all forces in this zone.
      destroyAllInZone(game, zoneID, forceIDOfDestroyAll);
    }
    destroyAllTerminateIfAttacked(game, zoneID);
  }
}


const takeShots = (
  game: Game, isDefender: boolean, shooters: number[][], opponents: number, targets: number[][][], 
) => {
  const players = game.players;
  const hits: number[] = [];  // Array of ForceIDs of the force that fired the successful shot.

  for(let i = shooters.length - 1; i >= 0; i--) {
    const forceType = shooters[i][BF.FORCE_INDEX_TYPE];

    const percent = isDefender ? game.forceDefense[forceType] : game.forceAttack[forceType];
    if( percent <= 0 ) {
      continue;  // Does not have a chance to hit anything.
    }

    let shots = 1;
    if( game.forceFlags[forceType] & BF.FORCE_FLAG_ONESHOTPERTARGET ) {
      shots = opponents;
    }
    if(isDefender) {
      if(Array.isArray(game.forceDefenseMultiple)) {
        shots *= game.forceDefenseMultiple[forceType];
      }
    }
    else {
      if(Array.isArray(game.forceAttackMultiple)) {
        shots *= game.forceAttackMultiple[forceType];
      }
    }

    if(percent <= 0) {
      continue;
    }
    for(let s = 0; s < shots; s++) {
      if( Math.random() * 100 <= percent ) {
        hits.push(shooters[i][BF.FORCE_INDEX_ID]);
      }
    }
  }

  // Assign the hits:
  for(let hit = 0, len = hits.length; hit < len; hit++) {
    // Select a random opponent to kill.
    const opponentOffset = Math.floor(Math.random() * (players + 1));
    for(let p = 0; p <= players; p++) { // All and 0.
      const opponentIndex = (p + opponentOffset) % (players + 1);
      if(targets[opponentIndex].length > 0) {
        if( findAndKill(game, targets[opponentIndex], opponentIndex, hits[hit]) ) {
          break;  // A successful kill was flagged.
        }
      }
    }
  }
}


function findAndKill (
  game: Game, targetList: number[][], targetPlayer: number, attackForceID: number,
) {
  // Selects the least valuable force (also incorporating casualtyPriority) to destroy.
  // Returns true if a target was found and flagged as destroyed.
  let bestIndex = -1;
  let bestScore = -1;
  let bestPriority = -1;

  let allyBits = game.fixedAlly[targetPlayer];

  for(let t = 0; t < targetList.length; t++) {
    const zoneID = targetList[t][BF.FORCE_INDEX_ZONE];
    if(zoneID <= 0) {
      continue;  // It is either cargo or already destroyed.
    }
    const movePoints = targetList[t][BF.FORCE_INDEX_MOVE_POINTS];
    const forceType = targetList[t][BF.FORCE_INDEX_TYPE];
    const forceFlags = game.forceFlags[forceType];

    // Kamakazi check, choose them first if appropriate:
    // If aircraft is out of range over enemy land (we do not do it for water, too many factors).
    if((forceFlags & BF.FORCE_FLAG_MUST_LAND_FRIENDLY) && movePoints === 0) {
      if(bfH.isLand(game, zoneID) && !((1 << game.zoneOwner[zoneID]) & allyBits)) {
        bestIndex = t;
        bestPriority = 1;
        bestScore = 1;
        break;  // Use it immediately, no sense searching for more as it will be dying.
      }
    }

    let score = game.forceCost[forceType] * 100;
    score += movePoints;

    // Add value of any cargo:
    const cargoZoneID = BF.FORCE_ZONE_CARGO - targetList[t][BF.FORCE_INDEX_ID];
    for(let i = 0, len = game.force[targetPlayer].length; i < len; i++) {
      if(game.force[targetPlayer][i][BF.FORCE_INDEX_ZONE] === cargoZoneID) {
        score += game.forceCost[game.force[targetPlayer][i][BF.FORCE_INDEX_TYPE]] * 100;
      }
    }

    const priority = 
      Array.isArray(game.forceCasualtyOrder) ? game.forceCasualtyOrder[forceType] : -1;

    // Utilize both the priority (if available) and the score of the force (secondary):
    if(bestIndex < 0 ||
      (priority > 0 && (bestPriority < 0 || priority < bestPriority)) ||
      (bestPriority === priority && score < bestScore)
    ) {
      bestIndex = t;
      bestPriority = priority;
      bestScore = score;
    }
  }

  if(bestIndex >= 0) {
    const forceID = targetList[bestIndex][BF.FORCE_INDEX_ID];
    bfTurnReplay.hit(game, forceID, attackForceID);
    targetList[bestIndex][BF.FORCE_INDEX_ZONE] = BF.FORCE_ZONE_DESTROYED;
    targetList[bestIndex][BF.FORCE_INDEX_MOVE_POINTS] = 0;

    // Destroy any cargo this force was carrying:
    const cargoZoneID = BF.FORCE_ZONE_CARGO - forceID;
    for(let i = 0; i < game.force[targetPlayer].length; i++) {
      if(game.force[targetPlayer][i][BF.FORCE_INDEX_ZONE] === cargoZoneID) {
        // Destroy the cargo.
        const cargoForce = game.force[targetPlayer][i];
        bfTurnReplay.hit(game, cargoForce[BF.FORCE_INDEX_ID], attackForceID);
        cargoForce[BF.FORCE_INDEX_ZONE] = BF.FORCE_ZONE_DESTROYED;
        cargoForce[BF.FORCE_INDEX_MOVE_POINTS] = 0;
      }
    }
    return true;
  }
  return false;
}

function destroyAllInZone (game: Game, zoneID: number, forceIDOfDestroyAll: number) {
  // Destroy all forces in zone, such as after a nuke.

  for(let playerIndex = 0; playerIndex <= game.players; playerIndex++) {  // All players and 0.
    const len = game.force[playerIndex].length;
    for(let j = 0; j < len; j++) {
      const force = game.force[playerIndex][j];
      if( force[BF.FORCE_INDEX_ZONE] !== zoneID ) {
        continue;
      }

      const forceType = force[BF.FORCE_INDEX_TYPE];
      const forceFlags = game.forceFlags[forceType];

      if( !(forceFlags & (BF.FORCE_FLAG_IS_SURFACE_ONLY_TARGET | BF.FORCE_FLAG_IS_AIR_ONLY_TARGET))) {
        continue;  // Indestructable, like a factory. Skip.
      }

      // Destroy the force:
      const forceID = force[BF.FORCE_INDEX_ID];
      bfTurnReplay.hit(game, forceID, forceIDOfDestroyAll);
      force[BF.FORCE_INDEX_ZONE] = BF.FORCE_ZONE_DESTROYED;
      force[BF.FORCE_INDEX_MOVE_POINTS] = 0;

      // Destroy any cargo this force was carrying:
      const cargoZoneID = BF.FORCE_ZONE_CARGO - forceID;
      for(let i = 0; i < game.force[playerIndex].length; i++) {
        if(game.force[playerIndex][i][BF.FORCE_INDEX_ZONE] === cargoZoneID) {
          // Destroy the cargo.
          const cargoForce = game.force[playerIndex][i];
          bfTurnReplay.hit(game, cargoForce[BF.FORCE_INDEX_ID], forceIDOfDestroyAll);
          cargoForce[BF.FORCE_INDEX_ZONE] = BF.FORCE_ZONE_DESTROYED;
          cargoForce[BF.FORCE_INDEX_MOVE_POINTS] = 0;
        }
      }

    }
  }
}


function destroyAllTerminateIfAttacked (game: Game, zoneID: number) {
  // Destory forces like missiles that automatically destruct when in combat.

  for(let playerIndex = 0; playerIndex <= game.players; playerIndex++) {  // All players and 0.
    const len = game.force[playerIndex].length;
    for(let j = 0; j < len; j++) {
      const force = game.force[playerIndex][j];
      if( force[BF.FORCE_INDEX_ZONE] !== zoneID ) {
        continue;
      }

      const forceType = force[BF.FORCE_INDEX_TYPE];
      const forceFlags = game.forceFlags[forceType];

      if( !(forceFlags & (BF.FORCE_FLAG_IS_SURFACE_ONLY_TARGET | BF.FORCE_FLAG_IS_AIR_ONLY_TARGET))) {
        continue;  // Indestructable, like a factory. Skip.
      }

      if( !(forceFlags & BF.FORCE_FLAG_TERMINATES_IF_ATTACKED)) {
        continue;  // Does not automatically terminate if attacked. Skip.
      }

      // Destroy the force:
      const forceID = force[BF.FORCE_INDEX_ID];
      bfTurnReplay.hit(game, forceID, forceID);
      force[BF.FORCE_INDEX_ZONE] = BF.FORCE_ZONE_DESTROYED;
      force[BF.FORCE_INDEX_MOVE_POINTS] = 0;

      // Destroy any cargo this force was carrying:
      const cargoZoneID = BF.FORCE_ZONE_CARGO - forceID;
      for(let i = 0; i < game.force[playerIndex].length; i++) {
        if(game.force[playerIndex][i][BF.FORCE_INDEX_ZONE] === cargoZoneID) {
          // Destroy the cargo.
          const cargoForce = game.force[playerIndex][i];
          bfTurnReplay.hit(game, cargoForce[BF.FORCE_INDEX_ID], forceID);
          cargoForce[BF.FORCE_INDEX_ZONE] = BF.FORCE_ZONE_DESTROYED;
          cargoForce[BF.FORCE_INDEX_MOVE_POINTS] = 0;
        }
      }

    }
  }
}


export function doEliminateAllMoveTerminateForces (game: Game) {
  // Destroy all 'if moved then auto-terminate' forces.

  for(let playerIndex = 0; playerIndex <= game.players; playerIndex++) {  // All players and 0.
    const len = game.force[playerIndex].length;
    for(let j = 0; j < len; j++) {
      const force = game.force[playerIndex][j];
      const zoneID = force[BF.FORCE_INDEX_ZONE];

      if(zoneID < 1) {
        continue;  // Not active, or is cargo or already destroyed. Skip.
      }

      const forceType = force[BF.FORCE_INDEX_TYPE];
      const forceFlags = game.forceFlags[forceType];

      if( !(forceFlags & (BF.FORCE_FLAG_IS_SURFACE_ONLY_TARGET | BF.FORCE_FLAG_IS_AIR_ONLY_TARGET))) {
        continue;  // Indestructable, like a factory. Skip.
      }

      if( !(forceFlags & BF.FORCE_FLAG_TERMINATES_IF_MOVED)) {
        continue;  // Does not automatically terminate if moved. Skip.
      }

      if(force[BF.FORCE_INDEX_MOVE_POINTS] === game.forceRange[forceType]) {
        continue;  // Force has not moved, so skip.
      }

      // Destroy the force:
      const forceID = force[BF.FORCE_INDEX_ID];
      bfTurnReplay.hit(game, forceID, forceID);
      force[BF.FORCE_INDEX_ZONE] = BF.FORCE_ZONE_DESTROYED;
      force[BF.FORCE_INDEX_MOVE_POINTS] = 0;

      // Destroy any cargo this force was carrying:
      const cargoZoneID = BF.FORCE_ZONE_CARGO - forceID;
      for(let i = 0; i < game.force[playerIndex].length; i++) {
        if(game.force[playerIndex][i][BF.FORCE_INDEX_ZONE] === cargoZoneID) {
          // Destroy the cargo.
          const cargoForce = game.force[playerIndex][i];
          bfTurnReplay.hit(game, cargoForce[BF.FORCE_INDEX_ID], forceID);
          cargoForce[BF.FORCE_INDEX_ZONE] = BF.FORCE_ZONE_DESTROYED;
          cargoForce[BF.FORCE_INDEX_MOVE_POINTS] = 0;
        }
      }
    }
  }

}
